import React, { useMemo, useState } from 'react';
import { Table, Typography, Alert, Input } from 'antd';
import { useTranslation } from 'react-i18next';
import { useGetChallengeUsers } from '@/hooks/users/useGetChallengeUsers';
import ModeSelector from './ModeSelector';
import TournamentConfig, { TournamentConfigValues } from './TournamentConfig';
import QuizDeadlineConfig, { QuizDeadlineValues } from './QuizDeadlineConfig';
import BracketPreview from './BracketPreview';
import { ChallengeMode, MIN_KNOCKOUT_PARTICIPANTS, MAX_KNOCKOUT_PARTICIPANTS, User } from '@/types';

const { Title } = Typography;
const { Search } = Input;

const columns = (t: any) => ([

  { title: t('table.firstName'), dataIndex: 'firstName', key: 'firstName' },
  { title: t('table.lastName'), dataIndex: 'lastName', key: 'lastName' },
  { title: t('table.email'), dataIndex: 'email', key: 'email' },
  { title: t('table.oib'), dataIndex: 'oib', key: 'oib' },
  { title: t('table.department') || 'Department / Org unit', dataIndex: 'department', key: 'department', render: (value: string | undefined) => value || '-' },
]);

const searchUser = (user: User, searchTerm: string) => {
  const normalizedSearchTerm = searchTerm.trim().toLowerCase();

  if (!normalizedSearchTerm) {
    return true;
  }

  return [
    user.firstName,
    user.lastName,
    user.email,
    user.oib,
    user.department,
  ]
    .filter(Boolean)
    .some(value => value!.toLowerCase().includes(normalizedSearchTerm));
};

interface UsersStepProps {
  selectedUserIds: string[];
  setSelectedUserIds: (ids: string[]) => void;
  mode: ChallengeMode;
  onModeChange: (mode: ChallengeMode) => void;
  tournamentConfig: TournamentConfigValues;
  onTournamentConfigChange: (config: TournamentConfigValues) => void;
  quizDeadline: QuizDeadlineValues;
  onQuizDeadlineChange: (config: QuizDeadlineValues) => void;
}

/**
 * Validates participant count for the selected challenge mode
 */
export function validateParticipantCount(
  mode: ChallengeMode,
  count: number
): { isValid: boolean; errorMessage: string | null } {
  if (mode === ChallengeMode.KnockoutTournament) {
    if (count < MIN_KNOCKOUT_PARTICIPANTS) {
      return {
        isValid: false,
        errorMessage: `Minimum ${MIN_KNOCKOUT_PARTICIPANTS} participants required for knockout tournament`,
      };
    }
    if (count > MAX_KNOCKOUT_PARTICIPANTS) {
      return {
        isValid: false,
        errorMessage: `Maximum ${MAX_KNOCKOUT_PARTICIPANTS} participants for knockout tournament. Use One-Time Matching for larger groups.`,
      };
    }
  } else {
    // One-Time Matching requires even number of participants (min 2)
    if (count < 2) {
      return {
        isValid: false,
        errorMessage: 'Minimum 2 participants required',
      };
    }
    if (count % 2 !== 0) {
      return {
        isValid: false,
        errorMessage: 'Participant count must be even for One-Time Matching',
      };
    }
  }
  return { isValid: true, errorMessage: null };
}

const UsersStep: React.FC<UsersStepProps> = ({ selectedUserIds, setSelectedUserIds, mode, onModeChange, tournamentConfig, onTournamentConfigChange, quizDeadline, onQuizDeadlineChange }) => {
  const { data, isLoading, isError, error } = useGetChallengeUsers();
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedUserIds(newSelectedRowKeys as string[]);
  };

  const validation = validateParticipantCount(mode, selectedUserIds.length);

  const filteredUsers = useMemo(() => {
    return (data?.items || []).filter(user => searchUser(user, searchTerm));
  }, [data?.items, searchTerm]);

  // Build participants list for bracket preview
  const selectedParticipants = useMemo(() => {
    if (!data?.items) return [];
    return data.items
      .filter(user => selectedUserIds.includes(user.id))
      .map(user => ({
        id: user.id,
        name: `${user.firstName} ${user.lastName}`.trim() || user.email,
      }));
  }, [data?.items, selectedUserIds]);

  return (
    <>
      <ModeSelector mode={mode} onModeChange={onModeChange} />
      {mode === ChallengeMode.KnockoutTournament && (
        <>
          <TournamentConfig config={tournamentConfig} onChange={onTournamentConfigChange} />
          {selectedParticipants.length >= MIN_KNOCKOUT_PARTICIPANTS && (
            <BracketPreview 
              participants={selectedParticipants} 
              enableThirdPlace={tournamentConfig.enableThirdPlace} 
            />
          )}
        </>
      )}
      {mode === ChallengeMode.OneTimeMatching && (
        <QuizDeadlineConfig config={quizDeadline} onChange={onQuizDeadlineChange} />
      )}
      {!validation.isValid && selectedUserIds.length > 0 && (
        <Alert
          message={validation.errorMessage}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}
      {isError && (
        <Alert
          message={error instanceof Error ? error.message : t('messages.error.getUsers')}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}
      <Title level={5}>{t('pages.quizWizard.selectUsers')}</Title>

      <Search
        allowClear
        placeholder={t('searchName') || 'Search users'}
        value={searchTerm}
        onChange={(event) => {
          setSearchTerm(event.target.value);
          setCurrentPage(1);
        }}
        style={{ marginBottom: 16, maxWidth: 420 }}
      />

      <Table
          rowKey={(record) => record.id}
          dataSource={filteredUsers}
          columns={columns(t)}
          size="small"
          loading={isLoading}
          rowSelection={{
            type: 'checkbox',
            selectedRowKeys: selectedUserIds,
            onChange: onSelectChange,
            preserveSelectedRowKeys: true,
          }}
          pagination={{
            current: currentPage,
            pageSize,
            total: filteredUsers.length,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total) => `${total} ${t('pages.quizWizard.selectedUsers') || 'participants'}`,
            onChange: (page, nextPageSize) => {
              setCurrentPage(page);
              if (nextPageSize !== pageSize) {
                setPageSize(nextPageSize);
              }
            },
          }}
        />
    </>
  );
};

export default UsersStep; 